import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormHelperText, Radio, TextField, Typography } from "@mui/material";
import Divider from "@mui/material/Divider";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { AnchorageArea } from "@services/vessel-call/types";
import { useTranslation } from "react-i18next";
import { getUploadEntryName } from "../attachmentUtils";
import { approveSchema } from "./approveSchema";

// This form does not have a store defined, so all fields will be managed via local state or props
// => use regular input components and manage their state locally or via props
interface ApproveVesselCallDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onDocumentUpload?: (file: File) => void;
  onAdditionalDocumentUpload?: (file: File) => void;
  onSubmit?: (payload: ApproveVesselCallPayload) => void;
  reEntryVessel?: boolean;
  overstayChargesApplicable?: boolean;
  expectedArrivalLocation?: string;
  preferredAnchorageArea?: string;
}

interface ApproveVesselCallPayload {
  reEntryVessel?: boolean;
  overstayChargesApplicable: boolean;
  assignedAnchorageArea?: string;
  externalNotes: string;
  internalNotes: string;
  hasInternalAttachment: boolean;
  hasExternalAttachment: boolean;
  externalAttachmentName?: string;
  internalAttachmentName?: string;
  status: "APPROVED";
}

const ApproveVesselCallDrawer: React.FC<ApproveVesselCallDrawerProps> = ({
  isOpen,
  onClose,
  onDocumentUpload,
  onAdditionalDocumentUpload,
  onSubmit,
  reEntryVessel,
  overstayChargesApplicable,
  expectedArrivalLocation,
  preferredAnchorageArea,
}) => {
  const { t } = useTranslation();
  const LANG_BASE = "vessel-call";
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const TEXTAREA_MAX_CHARS = 1000;
  const externalUploadSignatureRef = useRef<string | null>(null);
  const internalUploadSignatureRef = useRef<string | null>(null);
  const [externalNotes, setExternalNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [reEntry, setReEntry] = useState<boolean | undefined>(undefined);
  const [overstayCharges, setOverstayCharges] = useState<boolean | undefined>(undefined);
  const [assignedAnchorageArea, setAssignedAnchorageArea] = useState<string>(
    preferredAnchorageArea ?? "",
  );
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const showAnchorageAreaSelect = useMemo(
    () => ["FOAA", "DOAA"].includes(expectedArrivalLocation ?? ""),
    [expectedArrivalLocation],
  );
  const externalOnlySchema = approveSchema(t, showAnchorageAreaSelect).pick({
    externalNotes: true,
  });
  const internalOnlySchema = approveSchema(t, showAnchorageAreaSelect).pick({
    internalNotes: true,
  });
  const [errors, setErrors] = useState<{
    externalNotes?: string;
    internalNotes?: string;
    externalFile?: string;
    internalFile?: string;
    assignedAnchorageArea?: string;
  }>({});
  const fileProfiles = useFileUploadProfiles();
  const externalProfile = fileProfiles.sideDrawerApprovalFile;
  const internalProfile = externalProfile;

  const externalFiles = useUploadStore(
    (s) => s.filesByKey.ApproveExternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalFiles = useUploadStore(
    (s) => s.filesByKey.ApproveInternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const allowedExternalExtensions = externalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const removeInternalFile = useCallback(() => {
    clearFilesForKey("ApproveInternalAttachment");
  }, [clearFilesForKey]);

  const removeExternalFile = useCallback(() => {
    clearFilesForKey("ApproveExternalAttachment");
  }, [clearFilesForKey]);

  const clearFilesAndNotes = useCallback(() => {
    removeInternalFile();
    removeExternalFile();
    setExternalNotes("");
    setInternalNotes("");
    setReEntry(undefined);
    setOverstayCharges(undefined);
    setAssignedAnchorageArea(preferredAnchorageArea ?? "");
  }, [removeExternalFile, removeInternalFile, preferredAnchorageArea]);

  useEffect(() => {
    clearFilesAndNotes();
    setErrors({});
  }, [clearFilesAndNotes]);

  useEffect(() => {
    if (!isOpen) return;

    const defaultReEntry = reEntryVessel ?? false;
    setReEntry((prev) => prev ?? defaultReEntry);
    setOverstayCharges((prev) => prev ?? overstayChargesApplicable ?? defaultReEntry);
    setAssignedAnchorageArea((prev) => prev ?? preferredAnchorageArea);
  }, [
    isOpen,
    overstayChargesApplicable,
    reEntryVessel,
    preferredAnchorageArea,
    expectedArrivalLocation,
  ]);

  const handleClose = () => {
    onClose("approveDrawer");
  };

  const handleCancel = () => {
    clearFilesAndNotes();
    setErrors({});
    onClose("approveDrawer");
  };

  const setApproveExternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("ApproveExternalAttachment");
        return;
      }
      setFilesForKey("ApproveExternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setApproveInternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("ApproveInternalAttachment");
        return;
      }
      setFilesForKey("ApproveInternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | { externalFile?: string; internalFile?: string }
        | ((prev: { externalFile?: string; internalFile?: string }) => {
            externalFile?: string;
            internalFile?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ externalFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  const handleReEntryChange = (value: boolean) => {
    setReEntry(value);
  };

  const handleOverstayChargesChange = (value: boolean) => {
    setOverstayCharges(value);
  };

  const { options: anchorageAreas } = useFormattedReference<AnchorageArea>(
    REFERENCE_DATA_TYPES.ANCHORAGE_AREAS,
    "bff-vessel-call",
  );

  const anchorageAreasOptions = useMemo(
    () =>
      anchorageAreas
        .filter((area) => area.fromtoloc === expectedArrivalLocation)
        .map((area) => ({ label: area.anchorageAreaDesc, value: area.anchorageAreaCode }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [anchorageAreas, expectedArrivalLocation],
  );

  const handleSubmit = () => {
    const toValidate = {
      files: undefined,
      internalNotes,
      externalNotes,
      ...(showAnchorageAreaSelect ? { assignedAnchorageArea } : {}),
    };

    const validation = approveSchema(t, showAnchorageAreaSelect).safeParse(toValidate);
    if (!validation.success) {
      const flat = validation.error.flatten().fieldErrors;
      setErrors((prev) => ({
        ...prev,
        externalNotes: flat.externalNotes?.[0],
        internalNotes: flat.internalNotes?.[0],
        assignedAnchorageArea: flat.assignedAnchorageArea?.[0],
      }));
      return;
    }

    const hasInternalAttachment = internalFiles.length > 0;
    const hasExternalAttachment = externalFiles.length > 0;

    const payload: ApproveVesselCallPayload = {
      reEntryVessel: reEntry,
      overstayChargesApplicable: overstayCharges ?? false,
      externalNotes,
      internalNotes,
      hasInternalAttachment,
      hasExternalAttachment,
      externalAttachmentName: hasExternalAttachment
        ? getUploadEntryName(externalFiles[0]) || undefined
        : undefined,
      internalAttachmentName: hasInternalAttachment
        ? getUploadEntryName(internalFiles[0]) || undefined
        : undefined,
      status: "APPROVED",
      assignedAnchorageArea:
        showAnchorageAreaSelect && assignedAnchorageArea ? assignedAnchorageArea : undefined,
    };
    onSubmit?.(payload);
    clearFilesAndNotes();
    setReEntry(undefined);
    setAssignedAnchorageArea("");
  };

  return (
    <div data-testid="approve-vessel-call">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.approve.title`)}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t(`${LANG_BASE}.approve.approve-button`)}
        onSubmit={handleSubmit}
      >
        <Typography
          variant="body1"
          data-testid="approve-vessel-call-title"
          sx={{ color: "text.primary", px: 3, pb: 3 }}
        >
          {t(`${LANG_BASE}.approve.subtitle`)}
        </Typography>
        <div className="flex-1 max-w-sm py-2">
          <div className="flex flex-col gap-4 px-6">
            <div className="group">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.approve.re-entry`)}
              </Typography>
              <div className="flex flex-col gap-1">
                <div className="flex gap-8 items-center">
                  <div className="group">
                    <Radio
                      size="small"
                      checked={reEntry === true}
                      onChange={() => {
                        handleReEntryChange(true);
                      }}
                      aria-label="re-entry-yes"
                    />
                    <span>{t("common.yes")}</span>
                  </div>
                  <div className="group">
                    <Radio
                      size="small"
                      checked={reEntry === false}
                      onChange={() => {
                        handleReEntryChange(false);
                      }}
                      aria-label="re-entry-no"
                    />
                    <span>{t("common.no")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="group">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.approve.overstay-charges-from-day-1`)}
              </Typography>
              <div className="flex flex-col gap-1">
                <div className="flex gap-8 items-center">
                  <div className="group">
                    <Radio
                      size="small"
                      checked={overstayCharges === true}
                      onChange={() => {
                        handleOverstayChargesChange(true);
                      }}
                      aria-label="overstay-charges-from-day-1-yes"
                    />
                    <span>{t("common.yes")}</span>
                  </div>
                  <div className="group">
                    <Radio
                      size="small"
                      checked={overstayCharges === false}
                      onChange={() => {
                        handleOverstayChargesChange(false);
                      }}
                      aria-label="overstay-charges-from-day-1-no"
                    />
                    <span>{t("common.no")}</span>
                  </div>
                </div>
              </div>
            </div>
            {showAnchorageAreaSelect && (
              <div className="flex flex-col gap-2">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t(`${LANG_BASE}.approve.assigned-anchorage-area`)}
                </Typography>
                <Autocomplete
                  testId="approve-anchorage-area-select"
                  options={anchorageAreasOptions}
                  value={
                    anchorageAreasOptions.find(
                      (option) => option.value === assignedAnchorageArea,
                    ) || null
                  }
                  onChange={(option) => {
                    if (option?.value) {
                      setAssignedAnchorageArea(option.value as string);
                      setErrors((prev) => ({ ...prev, assignedAnchorageArea: undefined }));
                    } else {
                      setAssignedAnchorageArea("");
                    }
                  }}
                />
                {errors.assignedAnchorageArea && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {errors.assignedAnchorageArea}
                  </FormHelperText>
                )}
              </div>
            )}
            {/* External Notes */}
            <div className="flex flex-col gap-2 " data-testid="approve-external-notes">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.external-notes`)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={externalNotes}
                placeholder={t(`${LANG_BASE}.common.external-notes`)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (externalNotes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      externalNotes: t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.common.external-notes`),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = externalOnlySchema.safeParse({ externalNotes });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, externalNotes: flat.externalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, externalNotes: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setExternalNotes(val);
                  const res = externalOnlySchema.safeParse({ externalNotes: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, externalNotes: flat.externalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, externalNotes: undefined }));
                  }
                }}
              />
              {errors.externalNotes && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.externalNotes}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {externalNotes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
              <DocumentUpload
                label={t(`${LANG_BASE}.common.external-attachments`)}
                testId="approve-external-attachment-upload"
                name="ApproveExternalAttachment"
                maxFiles={1}
                allowedExtensions={allowedExternalExtensions}
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setErrors,
                    setUploadFiles: setApproveExternalAttachment,
                    onUploadDocument: onDocumentUpload,
                    uploadTracker: externalUploadSignatureRef,
                  })
                }
                conditions={externalProfile}
                files={externalFiles}
                setFiles={(next) => setFilesForKey("ApproveExternalAttachment", next)}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {errors.externalFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.externalFile}
                </FormHelperText>
              )}
              {externalFiles[0] && (
                <SingleFile
                  file={externalFiles[0]}
                  fieldName="ApproveExternalAttachment"
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, files: undefined }));
                    setApproveExternalAttachment([]);
                    externalUploadSignatureRef.current = null;
                  }}
                />
              )}
            </div>
            <Divider />
            {/* Internal Notes */}
            <div className="flex flex-col gap-2" data-testid="approve-internal-notes">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.internal-notes`)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={internalNotes}
                inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
                placeholder={t(`${LANG_BASE}.common.internal-notes`)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
                    setErrors((prev) => ({
                      ...prev,
                      internalNotes: t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.common.internal-notes`),
                        max: TEXTAREA_MAX_CHARS,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = internalOnlySchema.safeParse({ internalNotes });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setInternalNotes(val);
                  const res = internalOnlySchema.safeParse({ internalNotes: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                  } else {
                    setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                  }
                }}
              />
              {errors.internalNotes && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalNotes}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {internalNotes.length}/{TEXTAREA_MAX_CHARS}
              </FormHelperText>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.internal-attachments`)}
              </Typography>
              <DocumentUpload
                testId="approve-internal-attachment-upload"
                onUpload={(newFiles) =>
                  handleFileUpload({
                    newFiles,
                    setUploadErrors: setErrors,
                    setUploadFiles: setApproveInternalAttachment,
                    onUploadDocument: onAdditionalDocumentUpload,
                    uploadTracker: internalUploadSignatureRef,
                  })
                }
                name="ApproveInternalAttachment"
                conditions={internalProfile}
                files={internalFiles}
                setFiles={(next) => setFilesForKey("ApproveInternalAttachment", next)}
                maxFiles={1}
                allowedExtensions={allowedInternalExtensions}
                uploadDescription={t("upload.dragOrDropDescription")}
              />
              {errors.internalFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalFile}
                </FormHelperText>
              )}
              {internalFiles[0] && (
                <SingleFile
                  file={internalFiles[0]}
                  testId="approve-internal-attachment"
                  fieldName="ApproveInternalAttachment"
                  currentFileIndex={0}
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, files: undefined }));
                    setApproveInternalAttachment([]);
                    internalUploadSignatureRef.current = null;
                  }}
                  removeFn={removeInternalFile}
                />
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ApproveVesselCallDrawer;
